import React from "react";
import { MENTION_REGEX } from "../../../utils/messageUtils";
import styles from "./FormattedText.module.scss";

// URL regex pattern
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

interface FormattedTextProps {
  text: string;
  className?: string;
}

/**
 * Format mentions in message for display
 * Converts @[Name](userid:id) to clickable @Name
 */
const formatMentionsForDisplay = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;

  // Reset regex lastIndex
  MENTION_REGEX.lastIndex = 0;
  let match;

  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const [fullMatch, displayName, userId] = match;
    const matchIndex = match.index;

    // Add text before mention (with linkified URLs)
    if (matchIndex > lastIndex) {
      parts.push(...linkifyText(text.substring(lastIndex, matchIndex)));
    }

    // Add mention as clickable span
    parts.push(
      <span
        key={`mention-${userId}-${matchIndex}`}
        className={styles.mention}
        onClick={() => {
          // TODO: Handle mention click - open chat with user
          console.log("Clicked mention:", displayName, userId);
        }}
      >
        @{displayName}
      </span>,
    );

    lastIndex = matchIndex + fullMatch.length;
  }

  // Add remaining text (with linkified URLs)
  if (lastIndex < text.length) {
    parts.push(...linkifyText(text.substring(lastIndex)));
  }

  return parts.length > 0 ? parts : linkifyText(text);
};

/**
 * Convert URLs in text to clickable links
 */
const linkifyText = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;

  // Reset regex lastIndex
  URL_REGEX.lastIndex = 0;
  let match;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const url = match[0];
    const matchIndex = match.index;

    // Add text before URL
    if (matchIndex > lastIndex) {
      parts.push(text.substring(lastIndex, matchIndex));
    }

    // Add URL as clickable link
    parts.push(
      <a
        key={`url-${matchIndex}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.link}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );

    lastIndex = matchIndex + url.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

/**
 * Component to format text with clickable mentions and URLs
 * Use this for any message text that needs mention/link support
 */
const FormattedText: React.FC<FormattedTextProps> = ({ text, className }) => {
  return <span className={className}>{formatMentionsForDisplay(text)}</span>;
};

export default FormattedText;
